import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import MomentUtils from '@date-io/moment';
import { FormControlLabel, Grid, Radio, RadioGroup, Theme } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { useDialogContext, useFiltersContext } from 'core/FiltersDialog/FiltersDialogContext';
import { map } from 'lodash';
import { InlineDateTimePicker, MuiPickersUtilsProvider } from 'material-ui-pickers';
import { Moment } from 'moment';

import { Typography } from 'components/v2';

import { valueNormalizer } from './helpers';

const styles = ({
  custom: {
    palette: { colorSupportSnow },
  },
  spacing: { unit },
}: Theme) =>
  createStyles({
    radioGroup: {
      padding: `${unit * 5}px ${unit * 5}px 0 ${unit * 5}px`,
    },
    radio: {
      fontSize: 15,
    },
    customDateRange: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: 46,
      backgroundColor: colorSupportSnow,
    },
    rangeTextWrapper: {
      margin: `0 ${unit * 2}px`,
    },
    textFieldWrapper: {
      width: 220,
    },
    textFieldInput: {
      fontSize: 12,
      height: '36px !important',
    },
    textFieldHelperError: {
      position: 'absolute',
      top: 36,
    },
    padding: {
      padding: `${unit}px ${1.5 * unit}px`,
      paddingTop: 0,
    },
  });

const messages = defineMessages({
  any: {
    id: 'core.FiltersDialog.components.FiltersDate.any',
    defaultMessage: 'Any',
  },
  today: {
    id: 'core.FiltersDialog.components.FiltersDate.today',
    defaultMessage: 'Today',
  },
  yesterday: {
    id: 'core.FiltersDialog.components.FiltersDate.yesterday',
    defaultMessage: 'Yesterday',
  },
  pastWeek: {
    id: 'core.FiltersDialog.components.FiltersDate.pastWeek',
    defaultMessage: 'Last 7 days',
  },
  pastMonth: {
    id: 'core.FiltersDialog.components.FiltersDate.pastMonth',
    defaultMessage: 'Last 30 days',
  },
  pastYear: {
    id: 'core.FiltersDialog.components.FiltersDate.pastYear',
    defaultMessage: 'Last 365 days',
  },
  tomorrow: {
    id: 'core.FiltersDialog.components.FiltersDate.tomorrow',
    defaultMessage: 'Tomorrow',
  },
  nextWeek: {
    id: 'core.FiltersDialog.components.FiltersDate.nextWeek',
    defaultMessage: 'Next 7 days',
  },
  custom: {
    id: 'core.FiltersDialog.components.FiltersDate.custom',
    defaultMessage: 'Custom Date Range',
  },
});

export interface FiltersDateProps extends WithStyles<typeof styles> {
  filterName: string;
  options?: (
    | 'any'
    | 'today'
    | 'yesterday'
    | 'pastWeek'
    | 'pastMonth'
    | 'pastYear'
    | 'tomorrow'
    | 'nextWeek'
    | 'custom'
  )[];
  disablePast?: boolean;
}

const FiltersDate: React.FC<FiltersDateProps> = ({
  classes,
  filterName,
  options = ['today', 'yesterday', 'pastWeek', 'pastMonth', 'pastYear', 'custom'],
  disablePast,
}) => {
  const { formatMessage } = useIntl();
  const { filters, updater } = useFiltersContext();
  const { toggleLockFilters } = useDialogContext();

  const fallbackDateRange = filters[filterName];
  const fallbackSelectedDate = fallbackDateRange.selectedDate;
  const dateUpdater = updater(filterName);

  // State
  const [dateOption, setDateOption] = useState<string>(fallbackSelectedDate as string);
  const [selectedCustomDate, setSelectedCustomDate] = useState(() =>
    fallbackSelectedDate === 'custom'
      ? valueNormalizer('custom', fallbackDateRange)
      : valueNormalizer(fallbackSelectedDate),
  );

  // Force render when reset action occurs
  useEffect(() => {
    setDateOption(fallbackSelectedDate.toString());
  }, [fallbackSelectedDate]);

  const handleRadioClick = (value: string) => {
    toggleLockFilters(false);
    if (value !== 'custom') {
      dateUpdater(valueNormalizer(value));
    }
    setDateOption(value);
  };

  const handleCustomRange = (field: string, value: Moment) => {
    toggleLockFilters(false);
    setSelectedCustomDate({
      ...selectedCustomDate,
      [field]: value.valueOf(),
    });

    dateUpdater(
      valueNormalizer('custom', {
        ...selectedCustomDate,
        [field]: value.valueOf(),
      }),
    );
  };

  return (
    <>
      <Grid container spacing={8}>
        <Grid item>
          <RadioGroup
            className={classes.radioGroup}
            name="spacing"
            aria-label="Spacing"
            value={dateOption}
            onChange={(_, value) => handleRadioClick(value)}
            row
          >
            {map(options, (option) => (
              <FormControlLabel
                key={option}
                classes={{ label: classes.radio, root: classes.padding }}
                value={option}
                control={<Radio color="primary" />}
                label={formatMessage(messages[option])}
              />
            ))}
          </RadioGroup>
        </Grid>
      </Grid>
      {dateOption === 'custom' && (
        <div className={classes.customDateRange}>
          <div className={classes.textFieldWrapper}>
            <MuiPickersUtilsProvider utils={MomentUtils}>
              <InlineDateTimePicker
                keyboard
                ampm
                showTabs={false}
                value={selectedCustomDate.fromDate}
                onChange={(value) => handleCustomRange('fromDate', value)}
                disablePast={disablePast}
                format="MM/DD/YYYY hh:mm A"
                InputProps={{
                  classes: {
                    input: classes.textFieldInput,
                  },
                }}
                FormHelperTextProps={{
                  classes: {
                    error: classes.textFieldHelperError,
                  },
                }}
              />
            </MuiPickersUtilsProvider>
          </div>
          <div className={classes.rangeTextWrapper}>
            <Typography variant="body2">to</Typography>
          </div>
          <div className={classes.textFieldWrapper}>
            <MuiPickersUtilsProvider utils={MomentUtils}>
              <InlineDateTimePicker
                keyboard
                ampm
                showTabs={false}
                value={selectedCustomDate.toDate}
                onChange={(value) => handleCustomRange('toDate', value)}
                minDate={selectedCustomDate.fromDate}
                minDateMessage="Invalid Range"
                format="MM/DD/YYYY hh:mm A"
                InputProps={{
                  classes: {
                    input: classes.textFieldInput,
                  },
                }}
                FormHelperTextProps={{
                  classes: {
                    error: classes.textFieldHelperError,
                  },
                }}
              />
            </MuiPickersUtilsProvider>
          </div>
        </div>
      )}
    </>
  );
};

export default withStyles(styles)(FiltersDate);
